import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { EmptyPanel } from '@/shared/components/dashboard/panels';
import type { PlatformUsageSummary } from '../types';

interface RankedListProps {
  title: string;
  items: Array<{ label: string; count: number }>;
  emptyTitle: string;
  emptyMessage: string;
}

function RankedList({ title, items, emptyTitle, emptyMessage }: RankedListProps) {
  return (
    <Card className="min-w-0">
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {items.length === 0 ? (
          <div className="p-6">
            <EmptyPanel title={emptyTitle} message={emptyMessage} />
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {items.map((item) => (
              <li
                key={item.label}
                className="flex items-center justify-between gap-3 px-6 py-3 text-sm"
              >
                <span className="min-w-0 truncate text-foreground">{item.label}</span>
                <span className="shrink-0 tabular-nums font-medium text-muted-foreground">
                  {item.count}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

interface TopModulesPanelProps {
  topModules: PlatformUsageSummary['topModules'];
}

export function TopModulesPanel({ topModules }: TopModulesPanelProps) {
  const { t } = useTranslation();

  const items = topModules.map((row) => ({
    label: `${row.productSlug} · ${row.module}`,
    count: row.count,
  }));

  return (
    <RankedList
      title={t('platformUsage.topModules.title')}
      items={items}
      emptyTitle={t('platformUsage.topModules.emptyTitle')}
      emptyMessage={t('platformUsage.topModules.emptyMessage')}
    />
  );
}

interface TopPagesPanelProps {
  topPages: PlatformUsageSummary['topPages'];
}

export function TopPagesPanel({ topPages }: TopPagesPanelProps) {
  const { t } = useTranslation();

  const items = topPages.map((row) => ({
    label: `${row.productSlug} · ${row.pagePath}`,
    count: row.count,
  }));

  return (
    <RankedList
      title={t('platformUsage.topPages.title')}
      items={items}
      emptyTitle={t('platformUsage.topPages.emptyTitle')}
      emptyMessage={t('platformUsage.topPages.emptyMessage')}
    />
  );
}
